import { faCheck, faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { handleBundles, selectAddOns, setFourWeekPlacementFeature, setNewShowPlacementSupportModal, setPersonalisedSelected, setProMembershipSelected, setRating, setSkillName, setStudent } from '../../redux/addOnSlice'
import { useDispatch, useSelector } from 'react-redux'
import { selectPriceChart } from '../../redux/priceChartSlice'
import { resetSelectedItems, selectSelectedItems } from '../../redux/searchSlice'
import { nineNintyNine } from '.'
import { useAddonContext } from '../../context/AddonFeatureContext'
import { setPossibleSubjectModal, setShowCategoryModal, setShowCourseModal, setShowRelatedSubjectsModal } from '../../redux/otherDetailsSlice'
import { Mixpanel } from '../../config/Mixpanel'
import constants from '../../utilities/constants'

const NewAddOnSection = ({ skillName, visitedAnalysisCartPage, score }) => {

    const priceChart = useSelector(selectPriceChart)
    const selectedSkills = useSelector(selectSelectedItems)
    const addOns = useSelector(selectAddOns)

    const { threeCertificates, fiveCertificates, sevenCertificates, proMembershipSelected, personalisedSelected, fourWeekPlacementFeature, showFourWeekPlacementCard } = addOns
    const customBundleLength = selectedSkills?.length
    const customBundleSelected = customBundleLength > 0
    const skillIsBusinessManagement = nineNintyNine.includes(skillName?.toLowerCase())

    const [ showOptions, setShowOptions ] = useState(false)

    const { skillIsNotSeo } = useAddonContext()

    const dispatch = useDispatch()

    useEffect(() => {
        if(threeCertificates || fiveCertificates || sevenCertificates || proMembershipSelected || personalisedSelected || fourWeekPlacementFeature || customBundleSelected){
            setShowOptions(true)
        }
    }, [customBundleSelected, fiveCertificates, fourWeekPlacementFeature, personalisedSelected, proMembershipSelected, sevenCertificates, threeCertificates])

    const handleRelatedSubjectsModal = useCallback((e, number, subtitle) => {
        e.stopPropagation()
        dispatch(setShowRelatedSubjectsModal(true))
        dispatch(setPossibleSubjectModal({
            subjectNumber: number,
            subtitle
        }))
    },[dispatch])

    const handleSelectionOfBundles = useCallback((number) => {
        dispatch(handleBundles(number))
    }, [dispatch])

    const handlePersonaliseModal = useCallback((e) => {
        e.stopPropagation()
        dispatch(setRating(5))
        dispatch(setStudent(Math.floor((Math.random() * 1200) + 301)))
        dispatch(setSkillName(skillName))
        dispatch(setShowCourseModal(true))
    },[dispatch, skillName])

    const handleCategoryModal = useCallback((e) => {
        e.stopPropagation()
        dispatch(setShowCategoryModal(true))
    }, [dispatch])

    const handlePlacementSupportModal = useCallback((e) => {
        e.stopPropagation()
        dispatch(setNewShowPlacementSupportModal(true))
    }, [dispatch])

    const addOnContent = useMemo(() => {
        const content = []
        if((skillIsNotSeo || threeCertificates) && !sevenCertificates && !fiveCertificates){
            content.push({
                name: <span><span className='font-semibold'>3 More Certificates</span> In Subjects of Your Choice</span>,
                onClick:(e) => handleRelatedSubjectsModal(e, 'Any 3', 'Select any 3, post purchase, for a 48% chance to stand out.'),
                selected: threeCertificates,
                originalPrice: skillIsBusinessManagement ? priceChart[37]?.original_price : priceChart[2]?.original_price,
                discountPrice: skillIsBusinessManagement ? priceChart[37]?.price : priceChart[2]?.price,
                forSelection: () => handleSelectionOfBundles(1)
            })
        }
        if(fiveCertificates){
            content.push({
                name: <span><span className='font-semibold'>5 More Certificates</span> In Subjects of Your Choice</span>,
                onClick:(e) => handleRelatedSubjectsModal(e, 'Any 5', 'Select any 5, post purchase, for an 80% chance to stand out.'),
                selected: fiveCertificates,
                originalPrice: skillIsBusinessManagement ? priceChart[38]?.original_price : priceChart[10]?.original_price,
                discountPrice: skillIsBusinessManagement ? priceChart[38]?.price : priceChart[10]?.price,
                forSelection: () => handleSelectionOfBundles(2)
            })
        }
        if(sevenCertificates){
            content.push({
                name: <span><span className='font-semibold'>7 More Certificates</span> In Subjects of Your Choice</span>,
                onClick:(e) => handleRelatedSubjectsModal(e, 'Any 7', 'Select any 7, post purchase, for 2x more likely to be noticed by top MNCs & unicorns.'),
                selected: sevenCertificates,
                originalPrice: skillIsBusinessManagement ? priceChart[39]?.original_price : priceChart[11]?.original_price,
                discountPrice: skillIsBusinessManagement ? priceChart[39]?.price : priceChart[11]?.price,
                forSelection: () => handleSelectionOfBundles(3)
            })
        }
        if(customBundleSelected){
            content.push({
                name: `${customBundleLength} More Certificates in Subjects of Your Choice`,
                onClick: undefined,
                selected: customBundleSelected,
                originalPrice: customBundleLength * 199,
                discountPrice: Math.round((customBundleLength * (199) * (1 - (Math.min(customBundleLength * 5 /100, 0.5))))),
                forSelection: () => dispatch(resetSelectedItems())
            })
        }
        if(!visitedAnalysisCartPage || score > 30){
            content.push({
                name: <span>Personalised <span className='font-semibold'>{skillName}</span> Course Built For You Basis Your Quiz</span>,
                onClick: (e) => handlePersonaliseModal(e),
                selected: personalisedSelected,
                originalPrice: priceChart[4]?.original_price,
                discountPrice: priceChart[4]?.price,
                forSelection: () => dispatch(setPersonalisedSelected())
            })
        }
        content.push({
            name: <span>Unlimited Access to {skillName} & <span className='font-semibold'>1,240+ More Certified Courses</span></span>,
            onClick: (e) => handleCategoryModal(e),
            selected: proMembershipSelected,
            originalPrice: priceChart[3]?.original_price,
            discountPrice: priceChart[3]?.price,
            forSelection: () => dispatch(setProMembershipSelected())
        })
        if(fourWeekPlacementFeature || showFourWeekPlacementCard){
            content.push({
                name: <span>4-Week <span className='font-semibold'>Placement Training</span> To Get Hired 3X Faster</span>,
                onClick: (e) => handlePlacementSupportModal(e),
                selected: fourWeekPlacementFeature,
                originalPrice: priceChart[34]?.original_price,
                discountPrice: priceChart[34]?.price,
                forSelection: () => dispatch(setFourWeekPlacementFeature())
            })
        }
        return content
    }, [customBundleLength, customBundleSelected, dispatch, fiveCertificates, fourWeekPlacementFeature, handleCategoryModal, handlePersonaliseModal, handlePlacementSupportModal, handleRelatedSubjectsModal, handleSelectionOfBundles, personalisedSelected, priceChart, proMembershipSelected, score, sevenCertificates, showFourWeekPlacementCard, skillIsBusinessManagement, skillIsNotSeo, skillName, threeCertificates, visitedAnalysisCartPage])

    return (
        <div className='mt-3'>
            <h3 className='text-shadow cursor-pointer' onClick={() => {
                if(!showOptions){
                    Mixpanel.track(constants.Mixpanel.ANALYSIS_PAGE_ADD_ONS_BUTTON_WAS_CLICKED)
                }
                setShowOptions(!showOptions)
            }}><span className='text-sm font-medium'>Add Popular Benefits (Optional)</span> <FontAwesomeIcon icon={showOptions ? faChevronUp : faChevronRight} className='-mb-[2px]' /></h3>
            {showOptions && <div className='flex flex-col gap-2 mt-3 text-left'>
                {addOnContent?.map((addOn, index) => (
                    <div key={index} className='bg-bg-13/[15%] p-3 rounded-lg flex gap-3 items-center' onClick={addOn?.forSelection}>
                        <div className={`w-6 h-6 rounded-[2px] flex items-center justify-center border-[1.5px] border-shadow shadow-[0_0_2px_#7FC241] ${addOn?.selected && 'bg-shadow'}`}>
                            {addOn?.selected && <FontAwesomeIcon icon={faCheck} className='text-white' />}
                        </div>
                        <p className='flex-1 text-sm'>{addOn?.name} {addOn?.onClick && <img src="https://ik.imagekit.io/238r9emirjj/Info.png?updatedAt=1704268543580" style={{
                                display: 'inline'
                            }} alt="" width={16} height={16} className='cursor-pointer' onClick={(e) => addOn?.onClick(e)}/>}</p>
                        <div className='flex flex-col items-center'>
                            <p className='text-sm font-bold text-check-mark'>₹{addOn?.discountPrice}</p>
                            <p className='text-xs font-bold text-danger line-through'>₹{addOn?.originalPrice}</p>
                        </div>
                    </div>
                ))}
            </div>}
        </div>
    )
}

export default NewAddOnSection